import { MockAIProvider } from '../MockAIProvider';
import { appState } from './appState';
import { openByokDeleteModal } from './deleteModal';
import { dom } from './dom';
import { t } from './i18n';
import { store } from './store';
import { updateUI } from './ui';
const BYOK_PROVIDERS = [
    {
        id: 'anthropic',
        name: 'Anthropic',
        models: []
    },
    {
        id: 'google-gemini',
        name: 'Google Gemini',
        models: []
    },
    {
        id: 'xai',
        name: 'xAI',
        models: []
    },
    {
        id: 'openai',
        name: 'OpenAI',
        models: []
    }
];
const providerModelCache = new Map();
const mockAI = new MockAIProvider();
let closeModelDropdown = null;
export function renderByokProviders() {
    dom.byokProviderSelect.innerHTML = '';
    BYOK_PROVIDERS.forEach((provider, index) => {
        const option = document.createElement('option');
        option.value = provider.id;
        option.textContent = provider.name;
        if (index === 0) {
            option.selected = true;
        }
        dom.byokProviderSelect.appendChild(option);
    });
    const selectedProvider = dom.byokProviderSelect.value || BYOK_PROVIDERS[0]?.id || '';
    if (selectedProvider) {
        dom.byokProviderSelect.value = selectedProvider;
    }
    updateByokProviderModels(dom.byokProviderSelect.value);
}
export function updateByokProviderModels(providerId) {
    const provider = getByokProvider(providerId);
    dom.byokProviderModels.innerHTML = '';
    if (!provider) {
        dom.byokProviderModels.textContent = t('modals.byok.noProvider');
        return;
    }
    const keyEntry = getActiveByokKey(providerId);
    if (!keyEntry) {
        dom.byokProviderModels.textContent = t('modals.byok.addKeyToFetch');
        return;
    }
    if (provider.models.length === 0) {
        if (!providerModelCache.has(providerId)) {
            dom.byokProviderModels.textContent = t('modals.byok.fetchingModels');
            void ensureProviderModelsLoaded(providerId, keyEntry.key);
            return;
        }
        dom.byokProviderModels.textContent = t('modals.byok.noModels');
        return;
    }
    provider.models.forEach((model) => {
        const badge = document.createElement('span');
        badge.className = 'badge bg-secondary-subtle text-secondary';
        badge.textContent = model;
        dom.byokProviderModels.appendChild(badge);
    });
}
export function renderByokKeys() {
    const entries = store.getByokKeys();
    dom.byokKeysList.innerHTML = '';
    dom.byokKeyCount.textContent = entries.length.toString();
    if (entries.length === 0) {
        dom.byokEmptyState.classList.remove('d-none');
        return;
    }
    dom.byokEmptyState.classList.add('d-none');
    entries.forEach((entry) => {
        const provider = getByokProvider(entry.providerId);
        const providerName = provider?.name ?? entry.providerId;
        if (!providerModelCache.has(entry.providerId)) {
            void ensureProviderModelsLoaded(entry.providerId, entry.key);
        }
        const models = provider?.models.length
            ? provider.models.join(', ')
            : providerModelCache.has(entry.providerId)
                ? t('modals.byok.noModels')
                : t('modals.byok.fetchingModels');
        const row = document.createElement('div');
        row.className = 'byok-key-item';
        row.innerHTML = `
      <div class="d-flex flex-column gap-1">
        <div class="fw-semibold">${providerName}</div>
        <div class="byok-key-preview small text-muted">${maskApiKey(entry.key)}</div>
        <div class="byok-models">${models}</div>
      </div>
      <button class="btn btn-sm btn-outline-danger" type="button" title="${t('modals.byok.removeKeyTitle')}">
        <i class="bi bi-trash"></i>
      </button>
    `;
        const deleteBtn = row.querySelector('button');
        deleteBtn.addEventListener('click', () => {
            openByokDeleteModal(entry, providerName);
        });
        dom.byokKeysList.appendChild(row);
    });
}
export async function handleAddByokKey() {
    const providerId = dom.byokProviderSelect.value;
    const keyValue = dom.byokKeyInput.value.trim();
    if (!providerId) {
        alert(t('alerts.byokSelectProvider'));
        return;
    }
    if (!keyValue) {
        alert(t('alerts.byokEnterKey'));
        return;
    }
    store.addByokKey(providerId, keyValue);
    dom.byokKeyInput.value = '';
    await ensureProviderModelsLoaded(providerId, keyValue);
    renderByokKeys();
    renderModelDropdown();
    updateByokProviderModels(providerId);
    // Update UI to show BYOK toolbar buttons for anonymous users
    updateUI();
}
export async function loadByokModelCatalog() {
    const keys = store.getByokKeys();
    for (const entry of keys) {
        if (!providerModelCache.has(entry.providerId)) {
            await ensureProviderModelsLoaded(entry.providerId, entry.key);
        }
    }
}
export function buildAvailableModels() {
    // Collect all BYOK models first
    const byokModels = [];
    BYOK_PROVIDERS.forEach((provider) => {
        const keyEntry = getActiveByokKey(provider.id);
        if (!keyEntry) {
            return;
        }
        provider.models.forEach((model) => {
            byokModels.push({
                id: `byok:${provider.id}:${model}`,
                name: `BYOK: ${model}`,
                type: 'byok',
                providerId: provider.id,
                modelId: model
            });
        });
    });
    // If BYOK models exist, only return BYOK models (hide simulated)
    if (byokModels.length > 0) {
        return byokModels;
    }
    // Otherwise return simulated models
    const mockModels = mockAI.getModels().map((model) => ({
        id: model.id,
        name: model.name,
        type: 'mock'
    }));
    return mockModels;
}
export function renderModelDropdown() {
    const previousSelection = dom.modelSelector.value;
    appState.availableModels = buildAvailableModels();
    dom.modelList.innerHTML = '';
    let fallbackSelection = null;
    appState.availableModels.forEach((model, index) => {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.className = 'dropdown-item small';
        a.href = '#';
        a.dataset.modelId = model.id;
        a.textContent = model.name;
        a.onclick = (event) => {
            event.preventDefault();
            setSelectedModel(model);
            closeModelDropdown?.();
        };
        li.appendChild(a);
        dom.modelList.appendChild(li);
        if (model.id === previousSelection) {
            fallbackSelection = model;
        }
        else if (index === 0 && !fallbackSelection) {
            fallbackSelection = model;
        }
    });
    if (fallbackSelection) {
        setSelectedModel(fallbackSelection);
    }
}
export function initModelDropdown() {
    const dropdownElement = document.getElementById('modelDropdown');
    const dropdownMenu = document.getElementById('modelSelectorDropdown');
    const closeDropdown = () => {
        if (dropdownElement && dropdownMenu) {
            dropdownElement.setAttribute('aria-expanded', 'false');
            dropdownMenu.classList.remove('show');
        }
    };
    closeModelDropdown = closeDropdown;
    renderModelDropdown();
    if (dropdownElement && dropdownMenu) {
        dropdownElement.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            const isExpanded = dropdownElement.getAttribute('aria-expanded') === 'true';
            if (isExpanded) {
                closeDropdown();
            }
            else {
                dropdownElement.setAttribute('aria-expanded', 'true');
                dropdownMenu.classList.add('show');
            }
        });
        document.addEventListener('click', (event) => {
            if (!dropdownElement.contains(event.target) && !dropdownMenu.contains(event.target)) {
                closeDropdown();
            }
        });
    }
}
export async function sendByokMessage(context, model) {
    if (!model.providerId || !model.modelId) {
        throw new Error(t('errors.byokMissingDetails'));
    }
    const keyEntry = getActiveByokKey(model.providerId);
    if (!keyEntry) {
        throw new Error('No API key configured for this provider.');
    }
    switch (model.providerId) {
        case 'anthropic':
            return sendAnthropicRequest(context, model.modelId, keyEntry.key);
        case 'google-gemini':
            return sendGeminiRequest(context, model.modelId, keyEntry.key);
        case 'xai':
            return sendXaiRequest(context, model.modelId, keyEntry.key);
        case 'openai':
            return sendOpenAiRequest(context, model.modelId, keyEntry.key);
        default:
            throw new Error('Unsupported BYOK provider.');
    }
}
export async function sendMockMessage(context, modelId) {
    return mockAI.sendMessage(context, modelId);
}
// Helper to convert content to OpenAI format (array of content blocks)
function toOpenAIMessageContent(content) {
    if (typeof content === 'string') {
        return content;
    }
    return content.map(item => {
        if (item.type === 'text') {
            return { type: 'text', text: item.text };
        }
        else if (item.type === 'image') {
            // Extract base64 data without the data URL prefix
            const base64 = item.image?.replace(/^data:image\/\w+;base64,/, '') || '';
            return { type: 'image_url', image_url: { url: `data:image/jpeg;base64,${base64}` } };
        }
        return { type: 'text', text: '' };
    });
}
// Helper to convert content to Anthropic format
function toAnthropicMessageContent(content) {
    if (typeof content === 'string') {
        return content;
    }
    return content.map(item => {
        if (item.type === 'text') {
            return { type: 'text', text: item.text };
        }
        else if (item.type === 'image') {
            // Extract base64 data without the data URL prefix
            const base64 = item.image?.replace(/^data:image\/\w+;base64,/, '') || '';
            return { type: 'image', source: { type: 'base64', media_type: 'image/jpeg', data: base64 } };
        }
        return { type: 'text', text: '' };
    });
}
// Helper to convert content to Gemini format
function toGeminiMessageContent(content) {
    const parts = [];
    const contentArray = typeof content === 'string' ? [{ type: 'text', text: content }] : content;
    for (const item of contentArray) {
        if (item.type === 'text') {
            parts.push({ text: item.text });
        }
        else if (item.type === 'image') {
            // Gemini expects inlineData format
            const base64 = item.image?.replace(/^data:image\/\w+;base64,/, '') || '';
            const mimeType = item.image?.match(/^data:(\w+\/\w+);base64,/)?.[1] || 'image/jpeg';
            parts.push({ inlineData: { mimeType, data: base64 } });
        }
    }
    return parts;
}
async function sendAnthropicRequest(context, model, apiKey) {
    const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
            'content-type': 'application/json',
            'x-api-key': apiKey,
            'anthropic-version': '2023-06-01',
            'anthropic-dangerous-direct-browser-access': 'true'
        },
        body: JSON.stringify({
            model,
            max_tokens: 1024,
            system: context.systemPrompt,
            messages: context.messages.map(message => ({
                role: message.role === 'assistant' ? 'assistant' : 'user',
                content: toAnthropicMessageContent(message.content)
            }))
        })
    });
    if (!response.ok) {
        const errorText = await readErrorMessage(response);
        throw new Error(`Anthropic request failed: ${errorText}`);
    }
    const payload = await response.json();
    const content = payload?.content?.[0]?.text;
    if (!content) {
        throw new Error('Anthropic response missing content.');
    }
    return content;
}
async function sendGeminiRequest(context, model, apiKey) {
    const url = `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${encodeURIComponent(apiKey)}`;
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'content-type': 'application/json'
        },
        body: JSON.stringify({
            systemInstruction: {
                role: 'system',
                parts: [{ text: context.systemPrompt }]
            },
            contents: context.messages.map(message => ({
                role: message.role === 'assistant' ? 'model' : 'user',
                parts: toGeminiMessageContent(message.content)
            }))
        })
    });
    if (!response.ok) {
        const errorText = await readErrorMessage(response);
        throw new Error(`Gemini request failed: ${errorText}`);
    }
    const payload = await response.json();
    const content = payload?.candidates?.[0]?.content?.parts?.[0]?.text;
    if (!content) {
        throw new Error('Gemini response missing content.');
    }
    return content;
}
async function sendXaiRequest(context, model, apiKey) {
    const response = await fetch('https://api.x.ai/v1/chat/completions', {
        method: 'POST',
        headers: {
            'content-type': 'application/json',
            Authorization: `Bearer ${apiKey}`
        },
        body: JSON.stringify({
            model,
            messages: [
                { role: 'system', content: context.systemPrompt },
                ...context.messages.map(msg => ({
                    role: msg.role,
                    content: toOpenAIMessageContent(msg.content)
                }))
            ],
            temperature: 0.2
        })
    });
    if (!response.ok) {
        const errorText = await readErrorMessage(response);
        throw new Error(`xAI request failed: ${errorText}`);
    }
    const payload = await response.json();
    const content = payload?.choices?.[0]?.message?.content;
    if (!content) {
        throw new Error('xAI response missing content.');
    }
    return content;
}
async function sendOpenAiRequest(context, model, apiKey) {
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
            'content-type': 'application/json',
            Authorization: `Bearer ${apiKey}`
        },
        body: JSON.stringify({
            model,
            messages: [
                { role: 'system', content: context.systemPrompt },
                ...context.messages.map(msg => ({
                    role: msg.role,
                    content: toOpenAIMessageContent(msg.content)
                }))
            ],
            temperature: 0.2
        })
    });
    if (!response.ok) {
        const errorText = await readErrorMessage(response);
        throw new Error(`OpenAI request failed: ${errorText}`);
    }
    const payload = await response.json();
    const content = payload?.choices?.[0]?.message?.content;
    if (!content) {
        throw new Error('OpenAI response missing content.');
    }
    return content;
}
function getByokProvider(providerId) {
    return BYOK_PROVIDERS.find(provider => provider.id === providerId);
}
function getActiveByokKey(providerId) {
    return store.getByokKeys().find((entry) => entry.providerId === providerId) || null;
}
function setSelectedModel(model) {
    dom.modelSelector.value = model.id;
    dom.selectedModelName.textContent = model.name;
    dom.modelList.querySelectorAll('.dropdown-item').forEach((item) => item.classList.remove('active'));
    const activeItem = dom.modelList.querySelector(`[data-model-id="${model.id}"]`);
    activeItem?.classList.add('active');
}
function maskApiKey(key) {
    const trimmed = key.trim();
    if (trimmed.length <= 6) {
        return '••••••';
    }
    const start = trimmed.slice(0, 3);
    const end = trimmed.slice(-3);
    return `${start}${'•'.repeat(Math.max(6, trimmed.length - 6))}${end}`;
}
async function ensureProviderModelsLoaded(providerId, apiKey) {
    if (providerModelCache.has(providerId)) {
        return;
    }
    dom.byokProviderModels.textContent = t('modals.byok.fetchingModels');
    try {
        let models = [];
        switch (providerId) {
            case 'anthropic':
                models = await fetchAnthropicModels(apiKey);
                break;
            case 'google-gemini':
                models = await fetchGeminiModels(apiKey);
                break;
            case 'xai':
                models = await fetchXaiModels(apiKey);
                break;
            case 'openai':
                models = await fetchOpenAiModels(apiKey);
                break;
            default:
                throw new Error('Unsupported provider for model fetch.');
        }
        providerModelCache.set(providerId, models);
        const provider = getByokProvider(providerId);
        if (provider) {
            provider.models = models;
        }
        renderModelDropdown();
        renderByokKeys();
        updateByokProviderModels(providerId);
    }
    catch (error) {
        console.error('Failed to fetch BYOK models', error);
        dom.byokProviderModels.textContent = t('errors.byokModelsUnavailable');
    }
}
async function fetchAnthropicModels(apiKey) {
    const response = await fetch('https://api.anthropic.com/v1/models', {
        method: 'GET',
        headers: {
            'x-api-key': apiKey,
            'anthropic-version': '2023-06-01',
            'anthropic-dangerous-direct-browser-access': 'true'
        }
    });
    if (!response.ok) {
        const errorText = await readErrorMessage(response);
        throw new Error(`Anthropic model fetch failed: ${errorText}`);
    }
    const payload = await response.json();
    const rawModels = Array.isArray(payload?.data) ? payload.data.map((item) => item.id) : [];
    // Filter for popular chat models only (claude 3/4 series, exclude reasoning-only and legacy)
    const popularPrefixes = ['claude-opus-', 'claude-sonnet-', 'claude-haiku-'];
    const filtered = rawModels.filter((id) => popularPrefixes.some(prefix => id.startsWith(prefix)) ||
        id.includes('claude-3-') ||
        id.includes('claude-4-'));
    return normalizeModelList(filtered.length > 0 ? filtered : rawModels);
}
async function fetchGeminiModels(apiKey) {
    const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models?key=${encodeURIComponent(apiKey)}`);
    if (!response.ok) {
        const errorText = await readErrorMessage(response);
        throw new Error(`Gemini model fetch failed: ${errorText}`);
    }
    const payload = await response.json();
    const excludeKeywords = [
        'exp',
        'deep-research',
        'lite-preview',
        '-2025',
        '-2026',
        'robotics',
        'image',
        'tts',
        '001',
        '2.0-flash'
    ];
    const models = Array.isArray(payload?.models)
        ? payload.models
            .filter((item) => {
            const name = item.name?.toLowerCase() ?? '';
            return (name.includes('gemini') &&
                !excludeKeywords.some(keyword => name.includes(keyword)) &&
                item.supportedGenerationMethods?.includes('generateContent'));
        })
            .map((item) => item.name.replace('models/', ''))
        : [];
    return normalizeModelList(models);
}
// Popular xAI chat models (fallback when API fails due to CORS)
const FALLBACK_XAI_MODELS = [
    'grok-2',
    'grok-2-mini',
    'grok-3',
    'grok-3-mini',
    'grok-3-finance',
    'grok-beta'
];
async function fetchXaiModels(apiKey) {
    try {
        const response = await fetch('https://api.x.ai/v1/models', {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${apiKey}`
            }
        });
        if (!response.ok) {
            // If API fails, use fallback models
            console.warn('xAI models API returned error, using fallback models');
            return FALLBACK_XAI_MODELS;
        }
        const payload = await response.json();
        // xAI may return different response structure - handle both formats
        let rawModels = [];
        if (Array.isArray(payload?.data)) {
            rawModels = payload.data.map((item) => item.id);
        }
        else if (Array.isArray(payload?.models)) {
            rawModels = payload.models.map((item) => item.id || item.name);
        }
        else if (typeof payload?.data === 'object' && payload?.data !== null) {
            rawModels = Object.keys(payload.data);
        }
        // Filter for chat models only (grok series - include vision models)
        const filtered = rawModels.filter((id) => {
            const lower = id.toLowerCase();
            if (lower.includes('grok')) {
                // Include all grok models (including vision)
                return true;
            }
            return false;
        });
        return normalizeModelList(filtered.length > 0 ? filtered : rawModels);
    }
    catch (error) {
        // CORS or network error - use fallback models
        console.warn('xAI models fetch failed, using fallback models:', error);
        return FALLBACK_XAI_MODELS;
    }
}
async function fetchOpenAiModels(apiKey) {
    const response = await fetch('https://api.openai.com/v1/models', {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${apiKey}`
        }
    });
    if (!response.ok) {
        const errorText = await readErrorMessage(response);
        throw new Error(`OpenAI model fetch failed: ${errorText}`);
    }
    const payload = await response.json();
    const rawModels = Array.isArray(payload?.data) ? payload.data.map((item) => item.id) : [];
    // Strict filter: only keep base model names (no dated versions, no audio-specific, no codex)
    // Note: We include vision-capable models (gpt-4o, gpt-4o-mini, etc.)
    const excludeKeywords = ['preview', 'turbo', 'audio', 'realtime', 'search', 'tts', 'transcribe', 'diarize', 'codex', 'nano', 'deep-research'];
    const filtered = rawModels.filter((id) => {
        const lower = id.toLowerCase();
        // Must not have any excluded keywords
        if (excludeKeywords.some(k => lower.includes(k)))
            return false;
        // Must not have year dates (2024, 2025, 2026) - except for stable releases
        if (/\d{4}/.test(id))
            return false;
        // Keep only exact base model names (including vision-capable models)
        const validModels = [
            'gpt-4',
            'gpt-4.1',
            'gpt-4o',
            'gpt-4o-mini',
            'gpt-5',
            'gpt-5.1',
            'gpt-5.2',
            'gpt-5.3',
            'o1',
            'o1-pro',
            'o3',
            'o3-mini',
            'o3-pro'
        ];
        return validModels.includes(id);
    });
    return normalizeModelList(filtered.length > 0 ? filtered : rawModels);
}
function normalizeModelList(models) {
    return Array.from(new Set(models)).sort((a, b) => a.localeCompare(b));
}
async function readErrorMessage(response) {
    try {
        const payload = await response.json();
        if (typeof payload?.error?.message === 'string') {
            return payload.error.message;
        }
        if (typeof payload?.message === 'string') {
            return payload.message;
        }
        return JSON.stringify(payload);
    }
    catch (error) {
        try {
            return await response.text();
        }
        catch {
            return `HTTP ${response.status}`;
        }
    }
}
